import React, { useEffect, useRef, useState } from 'react';
import '../../styles/TextTyper.css'

type TextTyperProps = {
  messages: string[];
  isLoading?: boolean; // ✅ New prop
  width?: string;
  height?: string;
  typingSpeed?: number;
  fontSize?: string;
  fontWeight?: number | string;
  lineHeight?: string;
  color?: string;
  textAlign?: 'left' | 'center' | 'right' | 'justify';
  style?: React.CSSProperties;
  centered?: boolean;
};

const TextTyper: React.FC<TextTyperProps> = ({
  messages,
  isLoading = false,
  width = '100%',
  height = '300px',
  typingSpeed = 25,
  fontSize = '16px',
  fontWeight = 400,
  lineHeight = '24px',
  color = '#000',
  textAlign = 'left',
  centered,
  style = {},
}) => {
  const [typedLines, setTypedLines] = useState<string[]>([]);
  const [currentText, setCurrentText] = useState('');
  const [cursorVisible, setCursorVisible] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setCursorVisible((v) => !v);
    }, 500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (currentIndex >= messages.length) return;

    const message = messages[currentIndex];
    if (!message) return;

    if (message.startsWith('user_agent:')) {
      const cleanMessage = message.replace('user_agent:', '').trim();
      setTypedLines((prev) => [...prev, `user_agent:${cleanMessage}`]);
      setCurrentIndex((prev) => prev + 1);
      return;
    }

    let i = 0;
    let current = '';
    let cancelled = false;

    const typeChar = () => {
      if (cancelled) return;
      if (i < message.length) {
        current += message[i];
        setCurrentText(current);
        i++;
        setTimeout(() => requestAnimationFrame(typeChar), typingSpeed);
      } else {
        setTypedLines((prev) => [...prev, message]);
        setCurrentText('');
        setCurrentIndex((prev) => prev + 1);
      }
    };

    requestAnimationFrame(typeChar);
    return () => {
      cancelled = true;
    };
  }, [currentIndex, messages, typingSpeed]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [typedLines, currentText]);

  const combinedStyles: React.CSSProperties = {
    fontFamily: 'Montserrat',
    fontSize,
    fontWeight,
    lineHeight,
    color,
    textAlign,
    ...style,
  };

  return (
    <div
      style={
        centered
          ? {
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              height: '100%',
            }
          : {}
      }
    >
      <div
        ref={containerRef}
        style={{
          width,
          height,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          paddingRight: '8px',
        }}
        className="typer-scroll-container"
      >
        <div style={combinedStyles}>
          {typedLines.map((line, i) => {
            if (line.startsWith('user_agent:')) {
              return (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginTop: '4px',
                  }}
                >
                  <div
                    className="chat-bubble user"
                    style={{
                      backgroundColor: '#eaeaea',
                      color: '#222',
                      textAlign: 'right',
                      padding: '8px 12px',
                      borderRadius: '16px',
                      maxWidth: '75%',
                      wordWrap: 'break-word',
                      alignSelf: 'flex-end',
                      animation: 'slideInRightFade 0.3s ease-out',
                      boxShadow: '2px 2px 2px 2px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    {line.replace('user_agent:', '').trim()}
                  </div>
                </div>
              );
            } else {
              return (
                <div
                  className="chat-bubble ai"
                  key={i}
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    marginTop: '4px',
                  }}
                >
                  <div
                    className="chat-bubble ai"
                    style={{
                      backgroundColor: 'var(--primary)',
                      color: '#fff',
                      padding: '8px 12px',
                      borderRadius: '16px',
                      maxWidth: '75%',
                      wordWrap: 'break-word',
                      boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    {line}
                  </div>
                </div>
              );
            }
          })}

          {currentText && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-start',
                marginTop: '4px',
              }}
            >
              <div
                style={{
                  backgroundColor: 'var(--primary)',
                  color: '#fff',
                  padding: '8px 12px',
                  borderRadius: '16px',
                  maxWidth: '75%',
                  wordWrap: 'break-word',
                  boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.1)',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {currentText}
                <span style={{ opacity: cursorVisible ? 1 : 0 }}>|</span>
              </div>
            </div>
          )}

          {isLoading && currentIndex >= messages.length && !currentText && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-start',
                marginTop: '4px',
              }}
            >
              <div
                className="chat-bubble ai typing-indicator"
                style={{
                  backgroundColor: 'var(--primary)',
                  color: '#fff',
                  padding: '8px 16px',
                  borderRadius: '16px',
                  maxWidth: '75%',
                  display: 'flex',
                  gap: '4px',
                  alignItems: 'center',
                  height: '24px',
                }}
              >
                <span className="typing-dot" />
                <span className="typing-dot" />
                <span className="typing-dot" />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TextTyper;
